import type { default as React, FC } from "react";

import type { StateOrName } from "@uirouter/react";
import autobind from "autobind-decorator";
import _ from "lodash";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import type { IconName } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes, rgba, setFontVariant } from "@similarweb/styles";
import type { ButtonType, IDraggableItem } from "@similarweb/ui-components";
import {
    IconButton,
    ListItemSeparator,
    QueryBarWebsiteItem,
    findParentByClass,
    SingleDirectionDragAndDrop,
    ImageItemContainer,
    ItemContainer,
    PrimaryTextContainer,
    SecondaryTextContainer,
    DragHandle,
    Tooltip,
    FlexColumn,
} from "@similarweb/ui-components";

import { TOGGLE_OFF_COMPARE_DROPDOWN } from "app/action_types/website_analysis_action-types";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { QueryBarItem } from "app/components/compare/QueryBarItem";
import {
    FaviconButtonStyle,
    LoaderSection,
    OverlayMain,
    OverlaySidenav,
    OverlayTrialBanner,
    Separator,
    TopSection,
    TopSectionText,
} from "app/components/compare/StyledComponent";
import { usePageTopBanner } from "app/components/PageTopBanner/usePageTopBanner";
import { CircularLoader } from "app/components/React/CircularLoader/CircularLoader";
import { FaviconButtonContainer } from "app/components/React/FavIcon/FaviconButtonContainer";
import { withUIRouter } from "app/components/React/HOCs/withUIRouter";
import { WebsiteTooltip } from "app/components/React/Tooltip/WebsiteTooltip/WebsiteTooltip";
import { TRIAL_BANNER_ID } from "app/common/constants/trialBanner";
import { getIsSidebar3ClaimOpen } from "app/components/sidebar3/constants";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { isWebsiteAnalysisPage } from "app/modules/project-from-comparison/helpers/isWebsiteAnalysisPage";
import AddWebsiteToListButtonContainer from "app/pages/sales-intelligence/sub-modules/account-review/AddToListButton/AddWebsiteToListButtonContainer";
import { FavoritesService } from "app/services/favorites/favoritesService";
import { DefaultFetchService } from "app/services/fetchService";
import { ProductTours, ProductToursLocalStorageKeys, showIntercomTour } from "app/services/IntercomProductTourService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { fetchSimilarSitesQuery } from "app/services/sitesService/queries/sitesQueries";
import {
    getRecentsAnalysis,
    getRecentsByParentState,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import type { ISerializable } from "app/services/solutions2Services/HomepageDataFetchers/utils";
import { allTrackers } from "app/services/track/track";
import { AutocompleteWebsitesCompareItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitesCompareItem";
import { AutocompleteWebsitesMainItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitesMainItem";
import { AutocompleteWithLineOfBusiness } from "pro-features/components/AutocompleteWebsites/AutocompleteWithLineOfBusiness";
import type { IAutocompleteStyledProps } from "pro-features/components/AutocompleteWebsites/types";
import type { ISite } from "pro-features/components/Workspace/Wizard/src/types";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { BaseQueryBar, mapDispatchToProps, mapStateToProps } from "../BaseQueryBar";
import "../QueryBar.scss";
import { CreateCustomIndustryButton } from "../components/create-custom-industry-button/CreateCustomIndustryButton";
import { STATE_COMPARE_PREFIX } from "../QueryBarUtils";
import { autocompleteStates } from "../WebsiteQueryBarTypes";

import { isWebsiteCompareLocked, openLockedCompareHook, runWithCompareQueryGuard } from "./compareAccess";
import { MAX_COMPARABLE_ITEMS } from "./compareLimits";
import { KeyCompetitorsChip } from "./KeyCompetitorsChip/KeyCompetitorsChip";
import {
    getKeyCompetitorSuggestions,
    hasCompletedWebsiteCompare,
    isRegisteredAfterCompareSuggestionRelease,
    markWebsiteCompareCompleted,
    shouldShowKeyCompetitorsSuggestion,
    shouldWriteWebsiteCompareCompleted,
} from "./keyCompetitorsSuggestion";
import type { LobData } from "./useLobSites";
import type { IChosenItem } from "./WebsitePageQueryBarUtils";

declare const window;

enum ListItemsTypes {
    RECENT = "recent",
    SIMILAR = "similar",
    QUERY = "query",
}
export { MAX_COMPARABLE_ITEMS } from "./compareLimits";
export const BREAK_POINT = 1410;

export const hoverMessagesObject = {
    hoverTitleFavoriteAdd: "solutions2.tooltip.favorites.title.website.add",
    hoverTitleFavoriteRemove: "solutions2.tooltip.favorites.title.website.remove",
    addSuccessToastMessage: "tooltip.favorites.website.add.confirmation",
    addErrorToastMessage: "tooltip.favorites.add.error",
    removeSuccessToastMessage: "tooltip.favorites.website.remove.confirmation",
    removeErrorToastMessage: "tooltip.favorites.remove.error",
};

export const DragHandleStyled = styled(DragHandle)`
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    height: 34px;
`;

export const getDragBox = (
    _,
    isDragging: boolean,
    currentIndex,
    newIndex: number,
    shouldHideVersus: boolean,
    handleColor: string = colorsPalettes.carbon[0],
) => styled.div`
    border-radius: 8px;
    min-width: 0;

    .QueryBar-vs {
        display: ${newIndex === 0 ? "flex" : "none"};
        align-items: center;
    }

    ${newIndex !== 0 &&
    css`
        margin-right: 16px;
    `}

    ${isDragging &&
    css`
        ${ItemContainer} {
            box-shadow: 0px 3px 5px 0px ${rgba(colorsPalettes.carbon[400], 0.12)};
            background-color: ${colorsPalettes.bluegrey[200]};
        }
        ${ImageItemContainer} {
            border-color: ${colorsPalettes.blue[400]};
        }
        ${PrimaryTextContainer},
        ${SecondaryTextContainer} {
            color: ${colorsPalettes.blue[400]};
        }
    `}

    ${shouldHideVersus &&
    css`
        .QueryBar-vs {
            opacity: 0;
        }
    `}

    &:hover {
        ${DragHandleStyled} {
            opacity: 1;
        }
        ${currentIndex === 1 &&
        css`
            ${DragHandleStyled} {
                background-color: ${handleColor};
                padding-left: 20px;
                left: -32px;
            }
        `}
    }
`;

export const WebsiteQueryBarContainer = styled.div`
    .Popup-Container-infoCard {
        top: 60px !important;
    }
`;

const TooltipContentContainer = styled(FlexColumn)`
    justify-content: space-between;
    gap: 8px;
`;

const TooltipText = styled.span`
    flex: 1;
    ${setFontVariant("tooltip", colorsPalettes.bluegrey[100])}
`;

const DismissButton = styled.a`
    ${setFontVariant("tooltip", colorsPalettes.blue[300])}
    cursor: pointer;
`;

export const SingleDirectionDragAndDropStyled = styled(SingleDirectionDragAndDrop)`
    display: flex;
    flex-direction: row;
    min-width: 0;

    > div {
        min-width: 0;
    }
`;

const LoadingContainer = styled.div<IAutocompleteStyledProps>`
    width: ${({ width }) => (width ? `${width}px` : "248px")};
    max-height: 380px;
    position: ${({ position }) => (position ? position : "absolute")};
    z-index: 1030;
    left: 0;
    top: ${({ top }) => (top ? `${top}px` : "-8px")};
    border: 1px solid ${colorsPalettes.carbon[50]};
    background-color: ${colorsPalettes.carbon[0]};
    border-radius: 4px;
    box-shadow: 0 8px 8px 0 ${rgba(colorsPalettes.black[0], 0.24)};
`;

const OVERLAY_MAIN_OFFSET = 60;

export const PageOverlay = ({
    isOpen,
    hideSidebarOverlay = false,
    coverQueryBar = false,
}: {
    isOpen: boolean;
    hideSidebarOverlay?: boolean;
    coverQueryBar?: boolean;
}) => {
    const { topBannerHeight } = usePageTopBanner();
    // for trial packages (CHT-20)
    const trialBannerElement = document.getElementById(TRIAL_BANNER_ID);

    const isSidebar3Mode = getIsSidebar3ClaimOpen() && !proNavigator.isSalesSolution();
    const isSubNavOpen = isSidebar3Mode && proNavigator.isSubNavOpen();
    const overlayTop = coverQueryBar ? topBannerHeight : topBannerHeight + OVERLAY_MAIN_OFFSET;

    return isOpen ? (
        <>
            {trialBannerElement ? (
                <OverlayTrialBanner
                    height={topBannerHeight}
                    isSidebar3={isSidebar3Mode}
                    isSubNavOpen={isSubNavOpen}
                />
            ) : null}
            <OverlayMain
                top={overlayTop}
                isSidebar3={isSidebar3Mode}
                isSubNavOpen={isSubNavOpen}
            />
            {hideSidebarOverlay ? null : (
                <OverlaySidenav
                    isSidebar3={isSidebar3Mode}
                    isSubNavOpen={isSubNavOpen}
                />
            )}
        </>
    ) : null;
};

const loaderOptions = {
    svg: {
        stroke: `${colorsPalettes.midnight[50]}`,
        strokeWidth: "4",
        r: 16,
        cx: "50%",
        cy: "50%",
    },
    style: {
        width: 92,
        height: 92,
    },
};

export const LoadingComponent: FC<{
    placeholderText?: React.ReactNode;
    placeholderIconName?: IconName;
    headerText?: React.ReactNode;
    loaderStyles?: any;
}> = ({ placeholderText = "Start typing here...", placeholderIconName = "search", loaderStyles, headerText }) => (
    <LoadingContainer
        className="loading-component"
        {...loaderStyles}
    >
        <TopSection>
            <SWReactIcons iconName={placeholderIconName} />
            <TopSectionText>{placeholderText}</TopSectionText>
        </TopSection>
        <Separator />
        <ListItemSeparator>{headerText}</ListItemSeparator>
        <LoaderSection>
            <CircularLoader options={loaderOptions} />
        </LoaderSection>
    </LoadingContainer>
);

export interface ISimilarSite {
    Domain: string;
    DomainWithoutSub: string;
    Favicon: string;
}

interface IWebsiteQueryBarProps {
    onUserDidCompare?: () => void;
    onStateCompare?: (stateId: string) => void;
    userDidCompare?: boolean;
    statesDidCompare?: string[];
    chosenItems?: IChosenItem[];
    renderFavoriteButton?(domain: string): React.ReactNode;
    isCompareButtonOpen?: boolean;
    overrideCompareButton?: boolean;
    toggleCompareButton?: (type: object) => void;
    showCreateCustomIndustryCta?: boolean;
    filterListItems?: (items: ISite[]) => ISite[];
    filterRecentsBy?: StateOrName | StateOrName[];
    recentsDedupeFunction?: (recent: ISerializable) => string;
    lobData?: LobData;
    hideOverlay?: boolean;
    compareButtonType?: ButtonType;
    handleColor?: string;
    disableWebsiteTooltip?: boolean;
    tooltip?: {
        isVisible: boolean;
        content: string;
    };
    hideTooltip?: () => void;
    queryBarSearchHeader?: React.ReactNode;
}

interface IWebsiteQueryBarState {
    chosenItemsState: IChosenItem[];
    compareSitesToBeAdded: IChosenItem[];
    promoteCompareForState: boolean;
    recents: any;
    similarSites: any;
    /* a flag for determining if the add/compare button autocomplete is open */
    isCompareButton: boolean;
    /* used to determine which item's autocomplete is currently open and to populate the autocomplete placeholder */
    activeItem: string;
    isOverlayOpen: boolean;
    windowWidth: number;
    isFetching: boolean;
    listItemOrder: string[];
    /** whether to hide the Vs. sign between the first and compared items */
    shouldHideVersus: boolean;
    /** whether the user is currently dragging a query bar item */
    isDragging: boolean;
    isComparePreferenceReady: boolean;
    hasCompletedWebsiteCompare: boolean;
}

class WebsiteQueryBar extends BaseQueryBar<IWebsiteQueryBarProps, IWebsiteQueryBarState> {
    static defaultProps = {
        showCreateCustomIndustryCta: false,
        hideOverlay: false,
        compareButtonType: "primary",
    };

    private isUnmounted = false;

    private hasPersistedWebsiteCompare = false;

    private isPersistingWebsiteCompare = false;

    constructor(props) {
        super(props);
        this.services.chosenSites = chosenSites;
        this.services.swSettings = swSettings;
        this.services.translate = i18nFilter();
        this.services.fetchService = DefaultFetchService.getInstance();
        this.state = {
            promoteCompareForState: !this.didCurrentStateCompare(),
            chosenItemsState: this.props.chosenItems,
            compareSitesToBeAdded: [],
            similarSites: [],
            recents: [],
            isCompareButton: false,
            activeItem: null,
            isOverlayOpen: false,
            windowWidth: window.innerWidth,
            isFetching: false,
            listItemOrder: this.props.chosenItems.map((item) => item.name),
            shouldHideVersus: false,
            isDragging: false,
            isComparePreferenceReady: false,
            hasCompletedWebsiteCompare: false,
        };
    }

    public async componentDidMount() {
        super.componentDidMount();
        void this.loadComparePreference();
        document.body.addEventListener("click", this.handleBodyClick, {
            capture: true,
        });
        window.addEventListener(
            "resize",
            _.debounce(() => {
                this.setState({ windowWidth: window.innerWidth });
            }, 200),
            { capture: true },
        );
        this.setState({ isFetching: true });
        const recentItems = await this.getAndFilterRecentsAnalysis();
        const params = proNavigator.getParams();
        const similarSites = await this.fetchSimilarSites(
            this.state.chosenItemsState[0],
            params.country,
            params.webSource,
        );
        this.setState({
            recents: recentItems,
            similarSites,
            isFetching: false,
        });
        this.runIntercomTour(ProductTours.NewQueryBar, ProductToursLocalStorageKeys.NewQueryBarTour);
    }

    public componentWillUnmount() {
        this.isUnmounted = true;
        super.componentWillUnmount();
        document.body.removeEventListener("click", this.handleBodyClick, {
            capture: true,
        });
        window.removeEventListener(
            "resize",
            _.debounce(() => {
                this.setState({ windowWidth: window.innerWidth });
            }, 200),
            { capture: true },
        );
        this.props.toggleCompareButton({ type: TOGGLE_OFF_COMPARE_DROPDOWN });
    }

    public async componentDidUpdate(prevProps, prevState) {
        const params = proNavigator.getParams();
        const currentMainDomain = this.services.chosenSites.getPrimarySite();
        const previousMainDomain = prevProps.chosenItems[0];
        if (currentMainDomain.name !== previousMainDomain.name) {
            const recentItems = await this.getAndFilterRecentsAnalysis();
            const similarSites = await this.fetchSimilarSites(currentMainDomain, params.country, params.webSource);
            this.setState({
                recents: recentItems,
                similarSites,
            });
            setTimeout(() => {
                this.runIntercomTour(
                    ProductTours.NewQueryBar_Compare,
                    ProductToursLocalStorageKeys.NewQueryBar_CompareTour,
                );
            }, 10000);
        }
        // we check if every item's icon property is defined because it takes redux some time to update the item's
        // properties with their values and we don't want a broken image displayed in the interim.
        if (!_.isEqual(this.props.chosenItems, prevProps.chosenItems) && _.every(this.props.chosenItems, "icon")) {
            this.setState({ chosenItemsState: this.props.chosenItems });
        }
        if (prevProps.statesDidCompare !== this.props.statesDidCompare) {
            this.setState({
                promoteCompareForState: !this.didCurrentStateCompare(),
            });
        }
        if (this.props.isCompareButtonOpen) {
            // toggle was turned on  via Redux
            this.onCompareButtonClick(); // click on the button
        }
        if (this.state.isComparePreferenceReady && (this.isCompare() || this.state.chosenItemsState.length > 1)) {
            this.persistWebsiteCompareCompleted();
        }
    }

    public runIntercomTour = (productTourId: ProductTours, storageKey: ProductToursLocalStorageKeys): void => {
        const isTourAlreadySeen = localStorage.getItem(storageKey);

        if (isTourAlreadySeen === "true") {
            return;
        }

        showIntercomTour(productTourId);
        localStorage.setItem(storageKey, "true");
    };

    public fetchSimilarSites = async ({ name }, country = 999, webSource = "Total") => {
        try {
            const similarSites = await fetchSimilarSitesQuery(name as string, {
                limit: 20,
                country,
                webSource,
            });
            if (similarSites?.length === 0) {
                return [autocompleteStates.NO_DATA_SIMILARSITES];
            }
            return similarSites;
        } catch (e) {
            Sentry.errorTracker({
                e,
                message: "Error fetching similarSites - WebsiteQueryBar",
                level: "error",
                params: {
                    extra: { name, country, webSource },
                },
            });
            return [autocompleteStates.ERROR];
        }
    };

    private getAndFilterRecentsAnalysis = async () => {
        let recentItems;
        try {
            if (this.props.filterRecentsBy) {
                recentItems = getRecentsByParentState(
                    this.props.filterRecentsBy,
                    true,
                    this.props.recentsDedupeFunction,
                );
            } else {
                recentItems = await getRecentsAnalysis("website", true, this.props.recentsDedupeFunction);
            }
        } catch (e) {
            Sentry.errorTracker({
                e,
                message: "Error fetching recents - WebsiteQueryBar",
                level: "error",
                params: {
                    extra: { filterRecentsBy: this.props?.filterRecentsBy },
                },
            });
            return [autocompleteStates.ERROR];
        }
        if (recentItems.length === 0) {
            recentItems[0] = autocompleteStates.NO_DATA_RECENTS;
            return recentItems;
        }

        const withoutMainSite = recentItems.filter(
            (item) => item.data.mainItem !== this.state.chosenItemsState[0].name,
        );
        return withoutMainSite;
    };

    // this method is called when adding new sites from the compare button.
    private updateParamsWithNewSites = (newCompareList = this.state.compareSitesToBeAdded, addToExist = true) => {
        const newKeys = addToExist
            ? [
                  ...this.state.chosenItemsState.map((item) => item.name),
                  ...newCompareList.map((item) => item.name),
              ].join()
            : [...newCompareList.map((item) => item.name)].join();
        proNavigator.updateParams({ key: newKeys });
        if (newKeys.split(",").filter(Boolean).length > 1) {
            this.persistWebsiteCompareCompleted();
        }
    };

    private loadComparePreference = async (): Promise<void> => {
        await PreferencesService.whenReady();

        if (this.isUnmounted) {
            return;
        }

        this.setState(
            {
                isComparePreferenceReady: true,
                hasCompletedWebsiteCompare:
                    hasCompletedWebsiteCompare() ||
                    this.hasPersistedWebsiteCompare ||
                    this.state.hasCompletedWebsiteCompare,
            },
            () => {
                if (this.isUnmounted) {
                    return;
                }
                if (this.isCompare() || this.state.chosenItemsState.length > 1) {
                    this.persistWebsiteCompareCompleted();
                }
            },
        );
    };

    private persistWebsiteCompareCompleted = (): void => {
        if (
            !shouldWriteWebsiteCompareCompleted({
                isPreferencesReady: this.state.isComparePreferenceReady,
                hasPersistedWebsiteCompare: this.hasPersistedWebsiteCompare,
                hasCompletedWebsiteCompare: this.state.hasCompletedWebsiteCompare,
                isPersistingWebsiteCompare: this.isPersistingWebsiteCompare,
            })
        ) {
            return;
        }

        this.isPersistingWebsiteCompare = true;
        void markWebsiteCompareCompleted().then((didPersist) => {
            this.isPersistingWebsiteCompare = false;
            if (!didPersist || this.isUnmounted) {
                return;
            }

            this.hasPersistedWebsiteCompare = true;
            this.setState({ hasCompletedWebsiteCompare: true });
        });
    };

    private handleKeyCompetitorsSuggestionClick = (): void => {
        const stateName = proNavigator.current().name;
        if (this.shouldShowCompareHook()) {
            openLockedCompareHook("Locked key competitors suggestion", stateName);
            return;
        }

        const suggestions = getKeyCompetitorSuggestions(this.state.similarSites, this.state.chosenItemsState[0]?.name);
        if (suggestions.length === 0) {
            return;
        }

        const sitesToAdd = suggestions.map((site) => ({
            name: site.Domain,
            image: site.Favicon,
            icon: site.Favicon,
            displayName: site.Domain,
        })) as IChosenItem[];

        allTrackers.dispatchTrackEvent(
            "Compare",
            "click",
            `Key competitors suggestion/${stateName}/${sitesToAdd.map((site) => site.name).join(",")}`,
        );
        this.updateChosenItemsStateWithNewSites(sitesToAdd);
        this.updateParamsWithNewSites(sitesToAdd);
    };

    private renderKeyCompetitorsSuggestion = (): React.ReactNode => {
        const currentDomain = this.state.chosenItemsState[0]?.name;
        const sites = getKeyCompetitorSuggestions(this.state.similarSites, currentDomain);
        const isVisible = shouldShowKeyCompetitorsSuggestion({
            isPreferencesReady: this.state.isComparePreferenceReady,
            hasCompletedCompare: this.state.hasCompletedWebsiteCompare,
            isRegisteredAfterRelease: isRegisteredAfterCompareSuggestionRelease(
                this.services.swSettings.user?.createDate,
            ),
            isCompareMode:
                this.isCompare() ||
                this.state.chosenItemsState.length > 1 ||
                this.state.compareSitesToBeAdded.length > 0,
            isComparePickerOpen: this.state.isCompareButton,
            isCompareHidden:
                Boolean(proNavigator.current().hideCompareButton) || Boolean(this.props.overrideCompareButton),
            isSalesSolution: proNavigator.isSalesSolution(),
            suggestionCount: sites.length,
        });

        if (!isVisible) {
            return null;
        }

        return (
            <KeyCompetitorsChip
                sites={sites}
                onClick={this.handleKeyCompetitorsSuggestionClick}
            />
        );
    };

    // this method is called when adding new sites from the compare button.
    private updateChosenItemsStateWithNewSites = (
        compareSitesToBeAdded = this.state.compareSitesToBeAdded,
        addToExist = true,
    ) => {
        const updatedChosenItemsState = addToExist
            ? [...this.state.chosenItemsState, ...compareSitesToBeAdded]
            : [...compareSitesToBeAdded];
        if (this.props.isCompareButtonOpen) {
            // toggle was turned on via Redux
            this.props.toggleCompareButton({
                type: TOGGLE_OFF_COMPARE_DROPDOWN,
            });
        }
        this.setState({
            isCompareButton: false,
            activeItem: null,
            isOverlayOpen: false,
            shouldHideVersus: false,
            compareSitesToBeAdded: [],
            listItemOrder: updatedChosenItemsState.map((item) => item.name),
            chosenItemsState: updatedChosenItemsState,
        });
    };

    // this method is called when changing an existing compare site or the main site.
    private replaceKeyAndUpdateParams = (indexToReplace, replacementKey) => {
        // update params with new main site
        const keys = this.state.chosenItemsState.map((chosenItem) => chosenItem.name);
        // replace mainItem with new mainItem
        keys.splice(indexToReplace, 1, replacementKey);
        const keysString = keys.join(",");

        // sim-31888: reset params when changing main site
        const currentState = proNavigator.current();
        const params = { key: keysString, ...(currentState.reset ?? {}) };
        proNavigator.updateParams(params);
    };

    // this method is called when changing an existing compare site or the main site.
    private replaceItemAndUpdateState = (indexToReplace, replacementItem) => {
        const chosenItemsState = this.state.chosenItemsState.map((chosenItem) => chosenItem);
        chosenItemsState.splice(indexToReplace, 1, replacementItem);
        this.setState({
            isCompareButton: false,
            activeItem: null,
            isOverlayOpen: false,
            shouldHideVersus: false,
            chosenItemsState,
            listItemOrder: chosenItemsState.map((item) => item.name),
        });
    };

    private handleBodyClick = (e: MouseEvent) => {
        const targetElement = e.target as Element;
        if (
            !findParentByClass(targetElement, "AutocompleteWebsitesQueryBarContainer") &&
            !findParentByClass(targetElement, "AutocompleteWebsitesQueryBar") &&
            !findParentByClass(targetElement, "QueryBar-compare-button") &&
            !findParentByClass(targetElement, "loading-component")
        ) {
            // if in the process of adding compare sites and user clicks on a compare item (current or future), don't update params or state
            if (
                this.state.isCompareButton &&
                (findParentByClass(targetElement, "compare-site-to-be-added") ||
                    findParentByClass(targetElement, "querybar-item"))
            ) {
                return;
            }

            // logic for updating params if user was in the process of adding compare sites and clicked on body
            if (this.state.isCompareButton) {
                this.updateParamsWithNewSites();
            }

            this.updateChosenItemsStateWithNewSites();
        }
    };

    private onPressEnter = (event, item, isMainItem: boolean) => {
        if (typeof item !== "object") {
            return;
        } else if (item) {
            const type = event.target.value?.length ? "query" : "similar";
            if (isMainItem) {
                this.onMainItemAutocompleteItemClick(item, type);
            } else {
                this.onCompareItemAutocompleteItemClick(item, type);
            }
        } else {
            this.updateParamsWithNewSites();
            this.updateChosenItemsStateWithNewSites();
        }
    };

    private setActiveItem = (activeItemName) => {
        this.setState({
            isCompareButton: this.state.compareSitesToBeAdded.length > 0,
            activeItem: activeItemName,
            isOverlayOpen: true,
        });
        allTrackers.dispatchTrackEvent("External Link", "click", `${activeItemName || ""}`);
    };

    private onQueryButtonClick = (callback) => {
        void runWithCompareQueryGuard(callback);
    };

    public onMainItemTextClick = (name) => () => {
        // while the user is choosing compare sites, changing the mainItem is not allowed.
        if (this.state.isCompareButton && this.state.compareSitesToBeAdded.length > 0) {
            this.setState({ activeItem: null });
            return;
        }
        this.setActiveItem(name);
    };

    public onItemTextClick = (name) => () => {
        this.setActiveItem(name);
    };

    public trackItemClick = (
        name,
        type,
        guid: "query.bar.main.site.select.site" | "query.bar.compare.site.select.site",
    ) => {
        const typeToTrack = type === ListItemsTypes.QUERY ? type : ListItemsTypes.RECENT;
        const siteLabel = guid === "query.bar.main.site.select.site" ? "Main Site" : "Compare Site";
        allTrackers.dispatchTrackEvent("Search Bar", "click", `${typeToTrack || ""}/${siteLabel}/${name || ""}`);
    };

    public transformSite = (item: { domain: string; iconUrl: string }) => {
        return {
            name: item.domain,
            image: item.iconUrl,
        };
    };

    public onMainItemAutocompleteItemClick = (item, type) => {
        const comparedSites = item.compareItems?.map((site) => site.domain);
        const sites = !item.compareItems ? item.name : [item.mainSite.domain, comparedSites].join(",");
        this.trackItemClick(sites, type, "query.bar.main.site.select.site");
        if (item.compareItems) {
            const sitesToAdd = [];
            sitesToAdd.push(this.transformSite(item.mainSite));
            item.compareItems.forEach((compared) => {
                sitesToAdd.push(this.transformSite(compared));
            });
            this.updateChosenItemsStateWithNewSites(sitesToAdd, false);
            this.updateParamsWithNewSites(sitesToAdd, false);
        } else {
            // update querybar items visually. (when replacing the main item for some reason the color property is
            // not immediately passed to the new main item. Therefore, we add it manually)
            item.color = this.state.chosenItemsState[0].color;

            // update params with new main site
            this.replaceKeyAndUpdateParams(0, item.name);
            this.replaceItemAndUpdateState(0, item);
        }
    };

    public onCompareItemAutocompleteItemClick = (item, type) => {
        this.trackItemClick(item.name, type, "query.bar.compare.site.select.site");
        const itemIndex = this.state.chosenItemsState.findIndex(
            (chosenItem) => chosenItem.name === this.state.activeItem,
        );
        // if the user is choosing compare sites and replaces an existing this.state.chosenItemState item,
        // simply replace the item and wait till the user commits his choices and then update params.
        if (this.state.isCompareButton && this.state.compareSitesToBeAdded.length > 0) {
            const chosenItemsState = this.state.chosenItemsState.map((chosenItem) => chosenItem);
            chosenItemsState.splice(itemIndex, 1, item);
            this.setState({
                activeItem: null,
                listItemOrder: chosenItemsState.map((item) => item.name),
                chosenItemsState,
            });
            return;
        }

        this.replaceKeyAndUpdateParams(itemIndex, item.name);
        this.replaceItemAndUpdateState(itemIndex, item);
    };

    public onCompareButtonAutocompleteItemClick = (item, type) => {
        if (!item || typeof item !== "object") {
            this.updateChosenItemsStateWithNewSites();
            this.updateParamsWithNewSites();
            return;
        }
        this.trackItemClick(item.name, type, "query.bar.compare.site.select.site");
        // remove chosen site from similarsites and update state to refresh the sites in the autocomplete
        const similarSitesWithoutItem: ISimilarSite[] = this.state.similarSites.filter((x) => item.name !== x.Domain);
        this.setState({ similarSites: similarSitesWithoutItem });

        const compareSitesToBeAdded: IChosenItem[] = this.state.compareSitesToBeAdded.map((compareSite) => compareSite);
        item.displayName = item.name;
        compareSitesToBeAdded.push(item as IChosenItem);
        // if we are at max # of total items, update url params.
        if (this.state.chosenItemsState.length + compareSitesToBeAdded.length === 5) {
            this.updateChosenItemsStateWithNewSites(compareSitesToBeAdded);
            this.updateParamsWithNewSites(compareSitesToBeAdded);
            return;
        }
        // this piece of state is being set here to refresh the results of the similarSites in
        // the compare autocomplete (by way of the :excludes" prop) which will remove the site
        // that was currently chosen and to update the ui of the querybar with the added site.
        this.setState({
            compareSitesToBeAdded,
            listItemOrder: [
                ...this.props.chosenItems.map((item) => item.name),
                ...compareSitesToBeAdded.map((item) => item.name),
            ],
            isCompareButton: false,
        });
        // We have a unique functionality with the compare button autocomplete that when an item is selected
        // the autocomplete stays open and shifts over to allow the selected item to appear in the querybar.
        // Because it is shifting over, the click event target was being registered as the page itself and
        // not the item. To address this, a debounce was added to the setting of the state that causes the
        // autocomplete to shift (comparedItems).
        setTimeout(() => this.setState({ isCompareButton: true }), 50);
    };

    private shouldShowCompareHook(): boolean {
        return isWebsiteCompareLocked(this.services.swSettings);
    }

    private onCompareButtonClick = () => {
        if (this.shouldShowCompareHook()) {
            openLockedCompareHook("Locked compare", proNavigator.current().name);
            return;
        }

        allTrackers.dispatchTrackEvent("Compare", "open", "Header");
        this.props.onUserDidCompare();
        this.setState({
            isCompareButton: true,
            activeItem: null,
            isOverlayOpen: true,
        });
    };

    public iconButton = () => {
        return (
            <div>
                <IconButton
                    className={"QueryBar-compare-button"}
                    onClick={() => this.onQueryButtonClick(this.onCompareButtonClick)}
                    iconName="add"
                    type={this.props.compareButtonType}
                >
                    {this.getBtnText()}
                </IconButton>
            </div>
        );
    };

    private onCompareItemAutocompleteCloseButtonClick = (e) => {
        // if the user is choosing compare sites, close the existing chosenItems autocomplete
        if (this.state.isCompareButton) {
            this.setState({ activeItem: null });
            return;
        }

        this.setState({
            activeItem: null,
            isOverlayOpen: false,
        });
    };

    private onCompareButtonAutocompleteCloseButtonClick = (e) => {
        if (this.state.compareSitesToBeAdded.length > 0) {
            this.updateChosenItemsStateWithNewSites();
            this.updateParamsWithNewSites();
            return;
        }
        this.setState({
            isCompareButton: false,
            activeItem: null,
            isOverlayOpen: false,
            compareSitesToBeAdded: [],
        });
    };

    renderFavoriteButton(isAccountReview = false) {
        if (typeof this.props.renderFavoriteButton !== "function") {
            return (
                <FaviconButtonStyle>
                    {isAccountReview ? (
                        <AddWebsiteToListButtonContainer domain={this.state?.chosenItemsState[0]?.name} />
                    ) : (
                        <FaviconButtonContainer
                            hoverMessages={hoverMessagesObject}
                            isPageFavorite={FavoritesService.isCurrentPageFavorite()}
                            trackEventName={
                                (chosenSites.get().length > 1 ? "Comparison" : "Site") +
                                "/Website Analysis/" +
                                chosenSites.get().join()
                            }
                        />
                    )}
                </FaviconButtonStyle>
            );
        }

        return this.props.renderFavoriteButton(this.state.chosenItemsState[0]?.name);
    }

    private getButtonToDisplay(hasSelectedMaxNumOfCompetitors: boolean) {
        if (!hasSelectedMaxNumOfCompetitors) {
            return this.iconButton();
        } else if (this.props.showCreateCustomIndustryCta) {
            return <CreateCustomIndustryButton />;
        }
    }

    @autobind
    private changeQueryBarOrder(newColumns: IDraggableItem[]) {
        const reorderedColumnIds = newColumns.flatMap((column) => column.itemsIds ?? column.id);
        this.setState({ shouldHideVersus: false, isDragging: false });

        const didOrderChange = reorderedColumnIds.some((item, index) => this.state.listItemOrder[index] !== item);

        allTrackers.dispatchTrackEvent(
            "Switch",
            "click",
            `switch domains/main domain ${reorderedColumnIds[0] !== this.state.listItemOrder[0] ? "changed" : "didnt change"}`,
        );

        if (didOrderChange) {
            this.setState({ listItemOrder: reorderedColumnIds });
            proNavigator.updateQueryParams({ key: reorderedColumnIds.join(",") }, {});
        }
    }

    @autobind
    private handleDragStart() {
        this.setState({ isDragging: true, shouldHideVersus: true });
    }

    private handleTooltipDismiss = () => {
        this.props.hideTooltip?.();
    };

    private renderTooltipContent = () => (
        <TooltipContentContainer>
            <TooltipText
                dangerouslySetInnerHTML={{
                    __html: this.props.tooltip?.content,
                }}
            />
            <DismissButton onClick={this.handleTooltipDismiss}>
                {translate("autocomplete.websitesMain.tooltip.dismiss.button")}
            </DismissButton>
        </TooltipContentContainer>
    );

    private getLobData() {
        const {
            showLob = false,
            lobSites = [],
            isLobLoading = false,
            topAnalyzedLOBWebsites: topLobWebsites = [],
        } = this.props.lobData ?? {};

        const similarLobSites = this.state.similarSites
            .map((site) => lobSites.find((lob) => lob.name.toLowerCase() === site.Domain?.toLowerCase()))
            .filter(Boolean);

        return {
            showLob,
            lobSites,
            similarLobSites,
            isLobLoading,
            topLobWebsites,
        };
    }

    public render() {
        const {
            chosenItemsState,
            compareSitesToBeAdded,
            similarSites,
            isCompareButton,
            isOverlayOpen,
            windowWidth,
            isFetching,
            shouldHideVersus,
            listItemOrder,
        } = this.state;
        const isAccountReview = proNavigator.isAccountReview();

        const listItems = [this.getMainItem(), ...this.getCompareList()];
        const listItemsSorted = listItems.slice().sort((a, b) => {
            if (listItemOrder.indexOf(a.id) > listItemOrder.indexOf(b.id)) {
                return 1;
            }
            if (listItemOrder.indexOf(a.id) < listItemOrder.indexOf(b.id)) {
                return -1;
            }
            return 0;
        });

        // -1 is to remove the mainItem from the count
        const numOfVisibleCompareItems = chosenItemsState.length - 1 + compareSitesToBeAdded.length;
        const isSmallScreen = windowWidth <= BREAK_POINT;
        const { showLob, lobSites, similarLobSites, topLobWebsites, isLobLoading } = this.getLobData();
        return (
            <>
                <WebsiteQueryBarContainer className="QueryBar u-flex-row u-flex-center AutocompleteWebsitesQueryBarContainer">
                    {this.isCompare() || compareSitesToBeAdded.length > 0 ? (
                        <SingleDirectionDragAndDropStyled
                            className="u-flex-row u-flex-center"
                            direction="horizontal"
                            items={listItemsSorted}
                            getItemComponent={(...args) =>
                                getDragBox(...args, shouldHideVersus, this.props.handleColor)
                            }
                            onDragEnd={this.changeQueryBarOrder}
                            onDragStart={this.handleDragStart}
                        />
                    ) : (
                        <>
                            {listItemsSorted[0].component}
                            {this.renderFavoriteButton(isAccountReview)}
                        </>
                    )}
                    <div style={{ position: "relative" }}>
                        {!proNavigator.current().hideCompareButton &&
                        !isCompareButton &&
                        !this.props.overrideCompareButton
                            ? this.getButtonToDisplay(
                                  chosenItemsState.length + compareSitesToBeAdded.length >= MAX_COMPARABLE_ITEMS,
                              )
                            : null}
                        {isCompareButton && !this.props.overrideCompareButton ? (
                            isFetching ? (
                                <LoadingComponent
                                    loaderStyles={{
                                        ...(isSmallScreen && numOfVisibleCompareItems + 1 >= 4
                                            ? {
                                                  width: 208,
                                                  position: "relative",
                                                  top: 80,
                                              }
                                            : { top: "-24" }),
                                    }}
                                    placeholderText={"Enter a website..."}
                                    headerText={this.services.translate(
                                        "autocomplete.websitesCompare.seperator.text.similarSites",
                                    )}
                                />
                            ) : !showLob ? (
                                <AutocompleteWebsitesCompareItem
                                    className="AutocompleteWebsitesQueryBar"
                                    similarSites={similarSites}
                                    autocompleteProps={{
                                        placeholder: this.services.translate(
                                            "querybar.websites.comparebutton.autocomplete.placeholder",
                                        ),
                                        ...(isSmallScreen && numOfVisibleCompareItems + 1 >= 4
                                            ? {
                                                  width: 208,
                                                  position: "relative",
                                                  top: 4,
                                              }
                                            : { top: "-16" }),
                                    }}
                                    onClick={this.onCompareButtonAutocompleteItemClick}
                                    onCloseButtonClick={this.onCompareButtonAutocompleteCloseButtonClick}
                                    excludes={[...chosenItemsState, ...compareSitesToBeAdded]}
                                    overrideEnterFunc={(event, item) => {
                                        const type = event.target.value?.length ? "query" : "similar";
                                        this.onCompareButtonAutocompleteItemClick(item, type);
                                    }}
                                />
                            ) : (
                                <AutocompleteWithLineOfBusiness
                                    className="AutocompleteWebsitesQueryBar"
                                    autocompleteProps={{
                                        placeholder: this.services.translate(
                                            "querybar.websites.comparebutton.autocomplete.placeholder",
                                        ),
                                        ...(isSmallScreen && numOfVisibleCompareItems + 1 >= 4
                                            ? {
                                                  width: 208,
                                                  position: "relative",
                                                  top: 4,
                                              }
                                            : { top: "-16" }),
                                    }}
                                    onClose={this.onCompareButtonAutocompleteCloseButtonClick}
                                    onClick={(item) => this.onCompareButtonAutocompleteItemClick(item, "lob")}
                                    similarLobSites={similarLobSites}
                                    topAnalyzedLOBWebsites={topLobWebsites}
                                    lobSites={lobSites}
                                    isLoading={isLobLoading}
                                    excludes={[...this.state.chosenItemsState, ...this.state.compareSitesToBeAdded]}
                                    overrideEnterFunc={(event, item) => this.onPressEnter(event, item, true)}
                                />
                            )
                        ) : null}
                    </div>
                    {this.renderKeyCompetitorsSuggestion()}
                </WebsiteQueryBarContainer>
                {this.props.hideOverlay ? null : <PageOverlay isOpen={isOverlayOpen} />}
            </>
        );
    }

    protected getState() {
        return {
            promoteCompareForState: !this.didCurrentStateCompare(),
        };
    }

    private didCurrentStateCompare() {
        const stateParams = proNavigator.current();
        return this.props.statesDidCompare.indexOf(`${STATE_COMPARE_PREFIX}_${stateParams.name}`) > -1;
    }

    private isCompare() {
        return this.services.chosenSites.isCompare();
    }

    @autobind
    private closeDropdown() {
        this.setState({ isOverlayOpen: false, activeItem: null });
    }

    @autobind
    private getMainItem() {
        const mainItem = this.state.chosenItemsState[0];
        const showBadge = !this.state.isCompareButton && this.state.compareSitesToBeAdded.length === 0;
        const { showLob, lobSites, similarLobSites, isLobLoading, topLobWebsites } = this.getLobData();
        const content = (
            <FlexRow>
                <DragHandleStyled />
                <Tooltip
                    content={this.renderTooltipContent()}
                    enabled={this.props.tooltip?.isVisible}
                    open={this.props.tooltip?.isVisible}
                    placement="bottom"
                >
                    <span>
                        <QueryBarWebsiteItem
                            text={mainItem?.name}
                            image={mainItem?.icon ? mainItem?.icon : mainItem?.image}
                            onItemClick={() => this.onQueryButtonClick(this.onMainItemTextClick(mainItem?.name))}
                            onButtonClick={() => this.onQueryButtonClick(this.removeItem(mainItem))}
                            badgeColor={showBadge ? mainItem?.color : undefined}
                            isCompare={this.state.chosenItemsState.length > 1}
                        />
                    </span>
                </Tooltip>
                {this.isCompare() || this.state.compareSitesToBeAdded.length > 0 ? (
                    <span
                        key={0}
                        className="QueryBar-vs"
                        style={{ flex: "0 0 auto" }}
                    >
                        vs.
                    </span>
                ) : null}
            </FlexRow>
        );
        return {
            id: mainItem?.name,
            component: (
                <div
                    key={mainItem?.name}
                    className="u-flex-row u-flex-center QueryBar-mainItem"
                    style={{ flex: "0 0 auto" }}
                >
                    <QueryBarItem
                        id={mainItem?.name}
                        isLoading={this.state.isFetching}
                        activeItem={this.state.activeItem}
                        renderComponent={
                            this.props.disableWebsiteTooltip ? (
                                content
                            ) : (
                                <WebsiteTooltip
                                    domain={mainItem?.name}
                                    placement="bottom-left"
                                    debounce={1500}
                                    appendTo={".QueryBar"}
                                >
                                    {content}
                                </WebsiteTooltip>
                            )
                        }
                        searchComponent={
                            !this.isCompare() ? (
                                !showLob ? (
                                    <AutocompleteWebsitesMainItem
                                        className="AutocompleteWebsitesQueryBar"
                                        onClick={this.onMainItemAutocompleteItemClick}
                                        onClose={this.closeDropdown}
                                        recentSearches={this.state.recents}
                                        overrideEnterFunc={(event, item) => this.onPressEnter(event, item, true)}
                                        autocompleteProps={{
                                            placeholder: mainItem.name,
                                            width: 430,
                                        }}
                                        excludes={[...this.state.chosenItemsState]}
                                        showCompareSites={!proNavigator.current().hideCompareButton}
                                    >
                                        {this.props.queryBarSearchHeader}
                                    </AutocompleteWebsitesMainItem>
                                ) : (
                                    <AutocompleteWithLineOfBusiness
                                        className="AutocompleteWebsitesQueryBar"
                                        onClose={this.closeDropdown}
                                        autocompleteProps={{
                                            placeholder: mainItem.name,
                                            width: 430,
                                        }}
                                        onClick={(item) => this.onMainItemAutocompleteItemClick(item, "lob")}
                                        topAnalyzedLOBWebsites={topLobWebsites}
                                        lobSites={lobSites}
                                        isLoading={isLobLoading}
                                        excludes={[...this.state.chosenItemsState, ...this.state.compareSitesToBeAdded]}
                                        overrideEnterFunc={(event, item) => this.onPressEnter(event, item, true)}
                                    />
                                )
                            ) : !showLob ? (
                                <AutocompleteWebsitesCompareItem
                                    className="AutocompleteWebsitesQueryBar"
                                    onClick={this.onMainItemAutocompleteItemClick}
                                    onCloseButtonClick={this.closeDropdown}
                                    similarSites={this.state.similarSites}
                                    excludes={[...this.state.chosenItemsState]}
                                    overrideEnterFunc={(event, item) => this.onPressEnter(event, item, true)}
                                    autocompleteProps={{
                                        placeholder: mainItem?.name,
                                    }}
                                />
                            ) : (
                                <AutocompleteWithLineOfBusiness
                                    className="AutocompleteWebsitesQueryBar"
                                    onClose={this.closeDropdown}
                                    autocompleteProps={{
                                        placeholder: mainItem.name,
                                    }}
                                    onClick={(item) => this.onMainItemAutocompleteItemClick(item, "lob")}
                                    similarLobSites={similarLobSites}
                                    topAnalyzedLOBWebsites={topLobWebsites}
                                    lobSites={lobSites}
                                    isLoading={isLobLoading}
                                    excludes={[...this.state.chosenItemsState, ...this.state.compareSitesToBeAdded]}
                                    overrideEnterFunc={(event, item) => this.onPressEnter(event, item, true)}
                                />
                            )
                        }
                        loadingComponent={
                            <LoadingComponent
                                loaderStyles={{ width: 428 }}
                                placeholderText={mainItem?.name}
                                headerText={this.services.translate(
                                    this.state.chosenItemsState.length > 1
                                        ? "autocomplete.websitesCompare.seperator.text.similarSites"
                                        : "autocomplete.websitesMain.seperator.text.recents",
                                )}
                            />
                        }
                    />
                </div>
            ),
        };
    }

    @autobind
    private getCompareList(): IDraggableItem[] {
        const showBadge = !this.state.isCompareButton && this.state.compareSitesToBeAdded.length === 0;
        const isSmallScreen = this.state.windowWidth <= BREAK_POINT;
        const { showLob, similarLobSites, topLobWebsites, lobSites, isLobLoading } = this.getLobData();
        const currentCompareList = this.state.chosenItemsState.slice(1).map((item: IChosenItem, index) => {
            const content = (
                <FlexRow>
                    <DragHandleStyled />
                    <QueryBarWebsiteItem
                        text={item.name}
                        image={item.icon ? item.icon : item.image}
                        onItemClick={() => this.onQueryButtonClick(this.onItemTextClick(item.name))}
                        onButtonClick={() => this.onQueryButtonClick(this.removeItem(item))}
                        badgeColor={showBadge ? this.services.chosenSites.getSiteColor(item.name) : undefined}
                        isCompare={true}
                    />
                    {this.isCompare() || this.state.compareSitesToBeAdded.length > 0 ? (
                        <span
                            key={0}
                            className="QueryBar-vs"
                            style={{ flex: "0 0 auto" }}
                        >
                            vs.
                        </span>
                    ) : null}
                </FlexRow>
            );
            return (
                <div key={item.name}>
                    <QueryBarItem
                        id={item.name}
                        isLoading={this.state.isFetching}
                        activeItem={this.state.activeItem}
                        renderComponent={
                            this.props.disableWebsiteTooltip ? (
                                content
                            ) : (
                                <WebsiteTooltip
                                    domain={item.name}
                                    placement="bottom-left"
                                    debounce={1500}
                                    appendTo={".QueryBar"}
                                >
                                    {content}
                                </WebsiteTooltip>
                            )
                        }
                        searchComponent={
                            !showLob ? (
                                <AutocompleteWebsitesCompareItem
                                    className="AutocompleteWebsitesQueryBar"
                                    onClick={this.onCompareItemAutocompleteItemClick}
                                    similarSites={this.state.similarSites}
                                    excludes={[...this.state.chosenItemsState]}
                                    onCloseButtonClick={this.onCompareItemAutocompleteCloseButtonClick}
                                    overrideEnterFunc={(event, item) => this.onPressEnter(event, item, false)}
                                    autocompleteProps={{
                                        placeholder: `${this.state.activeItem ? this.state.activeItem : ""}`,
                                        ...(isSmallScreen && index === 3 ? { width: 208 } : {}),
                                    }}
                                />
                            ) : (
                                <AutocompleteWithLineOfBusiness
                                    className="AutocompleteWebsitesQueryBar"
                                    autocompleteProps={{
                                        placeholder: `${this.state.activeItem ? this.state.activeItem : ""}`,
                                        ...(isSmallScreen && index === 3 ? { width: 208 } : {}),
                                    }}
                                    onClick={(item) => this.onCompareItemAutocompleteItemClick(item, "lob")}
                                    similarLobSites={similarLobSites}
                                    topAnalyzedLOBWebsites={topLobWebsites}
                                    lobSites={lobSites}
                                    isLoading={isLobLoading}
                                    excludes={[...this.state.chosenItemsState, ...this.state.compareSitesToBeAdded]}
                                    overrideEnterFunc={(event, item) => this.onPressEnter(event, item, true)}
                                />
                            )
                        }
                        loadingComponent={
                            <LoadingComponent
                                loaderStyles={{
                                    ...(isSmallScreen && index === 3 ? { width: 208 } : {}),
                                }}
                                placeholderText={item.name}
                                headerText={this.services.translate(
                                    "autocomplete.websitesCompare.seperator.text.similarSites",
                                )}
                            />
                        }
                    />
                </div>
            );
        });
        const newCompareList = this.state.compareSitesToBeAdded.map((item: IChosenItem) => {
            return (
                <div key={item.name}>
                    <QueryBarItem
                        className="compare-site-to-be-added"
                        id={item.name}
                        renderComponent={
                            <QueryBarWebsiteItem
                                text={item.name}
                                image={item.icon}
                                onItemClick={() => null}
                                onButtonClick={this.removeItemFromSitesToBeAddedList(item.name)}
                                isCompare={true}
                            />
                        }
                    />
                </div>
            );
        });

        return [...currentCompareList, ...newCompareList].map((component: any) => ({
            id: component.key,
            component,
        }));
    }

    private removeItemFromSitesToBeAddedList = (item) => () => {
        const items = this.state.compareSitesToBeAdded.filter((compareSite) => compareSite.name !== item);
        this.setState({
            compareSitesToBeAdded: items,
            listItemOrder: [...this.props.chosenItems.map((item) => item.name), ...items.map((item) => item.name)],
        });
    };

    private debouncedUpdateChosenItemsParams = _.debounce((isMainSite) => {
        const key = this.state.chosenItemsState.map(({ name }) => name).join();
        const currentState = proNavigator.current();
        const params = {
            key,
            ...(isMainSite && currentState.resetOnMainSiteChange ? currentState.reset : {}),
            ...(proNavigator.isSalesSolution() ? { pageFilter: "" } : {}),
        };

        proNavigator.updateParams(params);
    }, 800);

    private removeItem = (item) => () => {
        // remove the item from the querybar visually. This logic is to have a quick
        // smooth instant replacement of the item while we wait for the params to update.
        // remove the item from the querybar visually. This logic is to have a quick
        // smooth instant replacement of the item while we wait for the params to update.
        let itemsWithoutRemovedItem;
        const isMainSite = item.name === this.state.chosenItemsState[0].name;
        if (isMainSite) {
            // if item was main item then replace it with the first item in comparedItems
            // state array and remove that compared item from the comparedItems.
            const currentComparedItemsArray = this.state.chosenItemsState.map((comparedItem) => comparedItem);
            // shift removes the first item and returns that item
            currentComparedItemsArray.shift();
            // add the current main items color value to the newMainItem for quick badge color update
            // (when replacing the main item for some reason the color property is
            // not immediately passed to the new main item. Therefore, we add it manually)
            currentComparedItemsArray[0].color = this.state.chosenItemsState[0].color;
            itemsWithoutRemovedItem = currentComparedItemsArray;
        } else {
            itemsWithoutRemovedItem = this.state.chosenItemsState.filter(
                (comparedItem) => comparedItem.name !== item.name,
            );
        }
        this.setState(
            {
                chosenItemsState: itemsWithoutRemovedItem,
                activeItem: null,
                listItemOrder: itemsWithoutRemovedItem.map((item) => item.name),
            },
            () => {
                this.debouncedUpdateChosenItemsParams(isMainSite);
            },
        );
    };

    private getBtnText() {
        if (isWebsiteAnalysisPage() && this.props.chosenItems.length <= 1) {
            return i18nFilter()("analysis.header.btn.compare.website");
        }

        if (this.props.chosenItems.length > 1) {
            return i18nFilter()("analysis.header.btn.add");
        } else {
            return i18nFilter()("analysis.header.btn");
        }
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withUIRouter(WebsiteQueryBar));
